import { useState } from "react";
import { Outlet, Link, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  Users,
  Building2,
  Layers,
  FileText,
  CreditCard,
  Megaphone,
  FolderOpen,
  TrendingUp,
  BarChart3,
  ShieldCheck,
  ClipboardCheck,
  ClipboardList,
  UserCog,
  Receipt,
  Settings2,
  Bell,
  Menu,
  X,
  User,
  LogOut,
  ChevronDown,
  HelpCircle,
} from "lucide-react";
import {
  cn,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  formatRelativeDate,
  ThemeToggle,
} from "@lsf/ui";
import { useAuth } from "@/lib/auth/AuthProvider";
import { useAdminNotificationsQuery } from "@/lib/api/queries";

// Only the four nav items that are wired in Phase 3b. Tenancies, Payments,
// Rent Reviews, Documents, Communications, Reports, Audit Logs, Settings —
// added back as their phase ships.
// `permission` is only set where the backend actually gates the module's
// list endpoint on it (verified against each Admin controller's index()) —
// omit it for modules any authenticated admin can view. Without this, a role
// that lacks the permission still sees the nav link, clicks through, and the
// backend's 403 renders as a misleading "no records" empty state.
// `area` groups the site map page (SiteMapPage.tsx reuses this exact array,
// not a duplicate list) — doesn't affect sidebar rendering.
// `permission` may be a single string or an array — an array means "visible
// if the user has ANY of these" (used by Applications, which bundles three
// separately-gated tabs into one page/nav entry).
export const navigation = [
  { name: "Dashboard", href: "/", icon: LayoutDashboard, exact: true, area: "Overview" },
  { name: "Tenants", href: "/tenants", icon: Users, area: "Tenants & Leasing" },
  { name: "Tenant Approval", href: "/tenants/approval", icon: Users, area: "Tenants & Leasing" },
  { name: "Tenancies", href: "/tenancies", icon: FileText, area: "Tenants & Leasing" },
  { name: "Applications", href: "/applications", icon: ClipboardList, permission: ["rental_applications.view", "kyc.view", "boarding.view"], area: "Tenants & Leasing" },
  { name: "Inspections", href: "/inspections", icon: ClipboardCheck, permission: "inspections.view", area: "Tenants & Leasing" },
  { name: "Flats", href: "/flats", icon: Building2, area: "Property" },
  { name: "Listing Features", href: "/feature-groups", icon: Layers, area: "Property" },
  { name: "Billing", href: "/billing", icon: CreditCard, area: "Billing & Finance" },
  { name: "Service Charge", href: "/service-charge", icon: Receipt, permission: "service_charge.view", area: "Billing & Finance" },
  { name: "Reports", href: "/reports", icon: BarChart3, permission: "reports.view", area: "Billing & Finance" },
  { name: "Communications", href: "/communications", icon: Megaphone, area: "Communications" },
  { name: "Documents", href: "/documents", icon: FolderOpen, area: "Communications" },
  { name: "Rent Reviews", href: "/rent-reviews", icon: TrendingUp, permission: "rent_reviews.view", area: "Operations" },
  { name: "Audit Log", href: "/audit", icon: ShieldCheck, permission: "audit.view", area: "Operations" },
  { name: "Users", href: "/users", icon: UserCog, permission: "users.view", area: "Administration" },
  { name: "Settings", href: "/settings", icon: Settings2, permission: "settings.view", area: "Administration" },
];

// Shared by AdminLayout's sidebar and SiteMapPage so both apply the same
// "any of" rule for array permissions, not two copies that could drift.
export function canViewNavItem(
  item: { permission?: string | string[] },
  hasPermission: (permission: string) => boolean,
): boolean {
  if (!item.permission) return true;
  const required = Array.isArray(item.permission) ? item.permission : [item.permission];
  return required.some((p) => hasPermission(p));
}

export function AdminLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const { user, logout, hasPermission } = useAuth();
  const { data: notifications } = useAdminNotificationsQuery();
  const unreadCount = notifications?.unread_count ?? 0;
  const visibleNavigation = navigation.filter((item) => canViewNavItem(item, hasPermission));

  return (
    <div className="flex h-screen bg-background">
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 lg:hidden z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <aside
        className={cn(
          "fixed inset-y-0 left-0 z-50 w-64 bg-sidebar transform transition-transform duration-200 ease-in-out lg:translate-x-0 lg:static lg:z-0",
          sidebarOpen ? "translate-x-0" : "-translate-x-full",
        )}
      >
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between h-16 px-6 border-b border-sidebar-border">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center">
                <img src="/logo.png" alt="LSF Estate" className="w-6 h-6 object-contain" />
              </div>
              <div>
                <div className="text-sidebar-foreground font-semibold">LSF Estate</div>
                <div className="text-xs text-sidebar-foreground/60">Management Portal</div>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden text-sidebar-foreground hover:text-sidebar-foreground/80"
              aria-label="Close sidebar"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
            {visibleNavigation.map((item) => {
              const isActive = item.exact
                ? location.pathname === item.href
                : location.pathname === item.href || location.pathname.startsWith(item.href + "/");
              const Icon = item.icon;

              return (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => setSidebarOpen(false)}
                  className={cn(
                    "flex items-center gap-3 px-3 py-2 rounded-lg transition-colors",
                    isActive
                      ? "bg-sidebar-accent text-sidebar-accent-foreground"
                      : "text-sidebar-foreground/70 hover:bg-sidebar-accent/50 hover:text-sidebar-foreground",
                  )}
                >
                  <Icon className="w-5 h-5" />
                  <span className="text-sm">{item.name}</span>
                </Link>
              );
            })}
          </nav>

          <div className="p-4 border-t border-sidebar-border">
            <div className="flex items-center gap-3 px-3 py-2 rounded-lg bg-sidebar-accent/50">
              <div className="w-8 h-8 rounded-full bg-accent flex items-center justify-center">
                <User className="w-4 h-4 text-accent-foreground" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm text-sidebar-foreground truncate">
                  {user?.full_name ?? "—"}
                </div>
                <div className="text-xs text-sidebar-foreground/60 capitalize">
                  {user?.role?.replace("_", " ")}
                </div>
              </div>
            </div>
          </div>
        </div>
      </aside>

      <div className="flex flex-col flex-1 overflow-hidden">
        <header className="h-16 bg-card border-b border-border flex items-center justify-between px-6">
          <div className="flex items-center gap-4">
            <button
              type="button"
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden text-foreground"
              aria-label="Open sidebar"
            >
              <Menu className="w-6 h-6" />
            </button>

          </div>

          <div className="flex items-center gap-4">
            <ThemeToggle />

            <Button variant="ghost" size="icon" asChild aria-label="Site map">
              <Link to="/sitemap">
                <HelpCircle className="w-5 h-5" />
              </Link>
            </Button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="relative" aria-label="Notifications">
                  <Bell className="w-5 h-5" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-medium text-destructive-foreground">
                      {unreadCount > 9 ? "9+" : unreadCount}
                    </span>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-80">
                <DropdownMenuLabel>Notifications</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {!notifications || notifications.items.length === 0 ? (
                  <div className="px-2 py-6 text-center text-sm text-muted-foreground">
                    You're all caught up.
                  </div>
                ) : (
                  notifications.items.map((item, i) => (
                    <DropdownMenuItem key={i} asChild className="flex flex-col items-start gap-0.5 whitespace-normal py-2">
                      <Link to={item.link} onClick={() => setSidebarOpen(false)}>
                        <span className="text-sm font-medium">{item.title}</span>
                        {item.detail && (
                          <span className="text-xs text-muted-foreground">{item.detail}</span>
                        )}
                        {item.timestamp && (
                          <span className="text-xs text-muted-foreground">
                            {formatRelativeDate(item.timestamp)}
                          </span>
                        )}
                      </Link>
                    </DropdownMenuItem>
                  ))
                )}
              </DropdownMenuContent>
            </DropdownMenu>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="gap-2">
                  <div className="w-8 h-8 rounded-full bg-accent flex items-center justify-center">
                    <User className="w-4 h-4 text-accent-foreground" />
                  </div>
                  <ChevronDown className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="font-medium">{user?.full_name}</div>
                  <div className="text-xs text-muted-foreground">{user?.email}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onSelect={() => logout()}
                  className="text-destructive focus:text-destructive"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto bg-background">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
